/**
 * Dashboard shell: auth + AdminLTE layout + route-specific views.
 * Routes: /admin (login), /dashboard, /conversations, /dashboard/config, /dashboard/admins
 */

import React, { useCallback, useEffect, useState } from 'react';
import { isSupabaseConfigured, DASHBOARD_AUTH_DOMAIN } from '../../lib/supabaseClient';
import { getApiBase } from '../../lib/apiBase';
import {
  ensureDashboardNetworkAccess,
  redirectIfDashboardIpDenied,
} from '../../lib/dashboardNetworkAccess';
import {
  getDashboardAdmin,
  getDashboardAuthHeaders,
  setDashboardSession,
  clearDashboardSession,
} from '../../lib/dashboardSession';
import { useAdminLTEAssets, setAdminLTEBodyMode } from '../../hooks/useAdminLTEAssets';
import { useDashboardPageScroll } from '../../hooks/useDashboardPageScroll';
import AdminLTELayout from './AdminLTELayout';
import DashboardHomeView from './DashboardHomeView';
import ConversationsView from './ConversationsView';
import FeedbackView from './FeedbackView';
import DashboardAdminPanel from '../DashboardAdminPanel';
import DashboardConfigView from './DashboardConfigView';
import '../../styles/dashboard-adminlte.css';

const APP_PATHS = new Set([
  '/admin',
  '/dashboard',
  '/conversations',
  '/dashboard/feedback',
  '/dashboard/config',
  '/dashboard/admins',
]);

function getDashboardPath() {
  if (typeof window === 'undefined') return '/dashboard';
  const p = window.location.pathname.replace(/\/$/, '') || '/dashboard';
  if (APP_PATHS.has(p)) return p;
  return '/dashboard';
}

function pageTitle(path) {
  if (path === '/conversations') return 'Conversations';
  if (path === '/dashboard/feedback') return 'Feedback';
  if (path === '/dashboard/admins') return 'Manage admins';
  if (path === '/dashboard/config') return 'Config status';
  if (path === '/admin') return 'Sign in';
  return 'Dashboard';
}

function breadcrumbLabel(path) {
  if (path === '/conversations') return 'Conversations';
  if (path === '/dashboard/feedback') return 'Feedback';
  if (path === '/dashboard/admins') return 'Admins';
  if (path === '/dashboard/config') return 'Config';
  return 'Overview';
}

function postLoginPath(fromPath) {
  if (fromPath === '/admin' || fromPath === '/dashboard') return '/dashboard';
  if (
    fromPath === '/conversations'
    || fromPath === '/dashboard/feedback'
    || fromPath === '/dashboard/admins'
    || fromPath === '/dashboard/config'
  ) {
    return fromPath;
  }
  return '/dashboard';
}

export default function DashboardApp() {
  useAdminLTEAssets();

  const [pathname, setPathname] = useState(getDashboardPath);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncingSheets, setSyncingSheets] = useState(false);
  const [networkChecking, setNetworkChecking] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [sessionMessages, setSessionMessages] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [loadingMessagesId, setLoadingMessagesId] = useState(null);

  useDashboardPageScroll(Boolean(user));

  const base = getApiBase();

  useEffect(() => {
    const onPopState = () => setPathname(getDashboardPath());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const allowed = await ensureDashboardNetworkAccess(base);
      if (cancelled || !allowed) return;
      const admin = getDashboardAdmin();
      if (!cancelled) {
        setUser(admin);
        setNetworkChecking(false);
        setAuthChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base]);

  useEffect(() => {
    if (!user && !authChecking) {
      setAdminLTEBodyMode({ login: true });
    }
    return () => {
      if (!user) setAdminLTEBodyMode({ login: false });
    };
  }, [user, authChecking]);

  useEffect(() => {
    if (user && pathname === '/admin') {
      window.history.replaceState(null, '', '/dashboard');
      setPathname('/dashboard');
    }
  }, [user, pathname]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Enter username and password');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${base}/api/dashboard/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (redirectIfDashboardIpDenied(res, data)) return;
      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }
      setDashboardSession(data.token, data.admin);
      setUser(data.admin);
      setPassword('');
      const target = postLoginPath(getDashboardPath());
      window.history.replaceState(null, '', target);
      setPathname(target);
      setRefreshToken((t) => t + 1);
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    clearDashboardSession();
    setUser(null);
    setUsername('');
    setPassword('');
    setSessionMessages({});
    setExpandedId(null);
    setError('');
    window.location.href = '/admin';
  };

  const handleRefresh = useCallback(() => {
    setRefreshToken((t) => t + 1);
  }, []);

  const handleSyncFromSheets = async () => {
    setSyncingSheets(true);
    setSyncMessage('');
    setError('');
    try {
      const res = await fetch(`${base}/api/dashboard/import-sheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getDashboardAuthHeaders(),
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (redirectIfDashboardIpDenied(res, data)) return;
      if (!res.ok) {
        setError(data.error || `Sync failed (${res.status})`);
        return;
      }
      setSyncMessage(
        `Synced ${data.sessions} session(s) and ${data.messages} message(s) from Google Sheets into the database.`
      );
      setRefreshToken((t) => t + 1);
    } catch (err) {
      setError(err?.message || 'Sync failed');
    } finally {
      setSyncingSheets(false);
    }
  };

  const alerts = (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
          {error}
        </div>
      )}
      {syncMessage && (
        <div className="alert alert-success alert-dismissible">
          <button type="button" className="close" onClick={() => setSyncMessage('')} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
          {syncMessage}
        </div>
      )}
    </>
  );

  const sharedSessionProps = {
    onError: setError,
    refreshToken,
    sessionMessages,
    setSessionMessages,
    expandedId,
    setExpandedId,
    loadingMessagesId,
    setLoadingMessagesId,
  };

  if (networkChecking || authChecking) {
    return (
      <div
        className="dashboard-adminlte d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-center">
          <i className="fas fa-spinner fa-spin fa-2x text-primary" />
          <p className="mt-3 text-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isSupabaseConfigured()) {
    return (
      <div className="dashboard-adminlte hold-transition login-page">
        <div className="login-box">
          <div className="login-logo">
            <a href="/dashboard">
              <b>Berwind</b> Dashboard
            </a>
          </div>
          <div className="card card-outline card-danger">
            <div className="card-body">
              <p className="mb-0">
                Supabase is not configured. Add REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY,
                SUPABASE_URL, and SUPABASE_SERVICE_ROLE_KEY to .env, then run supabase/schema.sql.
              </p>
              <p className="mt-3 mb-0">
                <a href="/">← Back to app</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard-adminlte hold-transition login-page">
        <div className="login-box">
          <div className="login-logo">
            <a href="/admin">
              <b>Berwind</b> Avatar
            </a>
          </div>
          <div className="card">
            <div className="card-body login-card-body">
              <p className="login-box-msg">Sign in to start your session</p>
              <form onSubmit={handleLogin}>
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                  />
                  <div className="input-group-append">
                    <div className="input-group-text">
                      <span className="fas fa-user" />
                    </div>
                  </div>
                </div>
                <div className="input-group mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <div className="input-group-append">
                    <div className="input-group-text">
                      <span className="fas fa-lock" />
                    </div>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-1" />
                      Signing in…
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
              {error && <p className="text-danger text-sm mt-3 mb-0">{error}</p>}
              <p className="mt-3 mb-0 text-muted text-sm text-center">
                Usernames without @ use @{DASHBOARD_AUTH_DOMAIN}
              </p>
            </div>
          </div>
          <p className="mt-3 text-center text-sm">
            <a href="/">← Back to app</a>
          </p>
        </div>
      </div>
    );
  }

  const path = pathname;
  const title = pageTitle(path);
  const crumb = breadcrumbLabel(path);

  return (
    <div className="dashboard-adminlte">
      <AdminLTELayout
        user={user}
        pathname={path}
        onRefresh={handleRefresh}
        onSyncSheets={handleSyncFromSheets}
        onSignOut={handleSignOut}
        loading={loading}
        syncingSheets={syncingSheets}
      >
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">{title}</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/dashboard">Home</a>
                  </li>
                  <li className="breadcrumb-item active">{crumb}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {alerts}

        {path === '/dashboard/admins' && <DashboardAdminPanel />}

        {path === '/dashboard/config' && (
          <DashboardConfigView onError={setError} refreshToken={refreshToken} />
        )}

        {path === '/dashboard' && <DashboardHomeView {...sharedSessionProps} />}

        {path === '/conversations' && (
          <ConversationsView onError={setError} refreshToken={refreshToken} />
        )}

        {path === '/dashboard/feedback' && (
          <FeedbackView onError={setError} refreshToken={refreshToken} />
        )}
      </AdminLTELayout>
    </div>
  );
}
